import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, orderBy, query, writeBatch,
} from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import AdminLayout from './AdminLayout'

// ── Weekday options (localized, Mon→Sun order) ────────────────────────────────
const WEEKDAYS = [
  { key: 'mon', ua: 'Понеділок', en: 'Monday',    icon: '📅', accent: '#6c757d' },
  { key: 'tue', ua: 'Вівторок',  en: 'Tuesday',   icon: '🙏', accent: '#f07a28' },
  { key: 'wed', ua: 'Середа',    en: 'Wednesday', icon: '📖', accent: '#d4a800' },
  { key: 'thu', ua: 'Четвер',    en: 'Thursday',  icon: '🎵', accent: '#9b59b6' },
  { key: 'fri', ua: 'П\'ятниця', en: 'Friday',    icon: '✝',  accent: '#3aaa6e' },
  { key: 'sat', ua: 'Субота',    en: 'Saturday',  icon: '👥', accent: '#3a8fd9' },
  { key: 'sun', ua: 'Неділя',    en: 'Sunday',    icon: '⛪', accent: '#7c5cbf' },
]

const DAY_ORDER = Object.fromEntries(WEEKDAYS.map((w, i) => [w.key, i]))

// ── Default data ──────────────────────────────────────────────────────────────
const DEFAULT_DAYS = [
  { key: 'tue', labelUa: 'Вівторок',  labelEn: 'Tuesday',   icon: '🙏', accent: '#f07a28', order: 1 },
  { key: 'wed', labelUa: 'Середа',    labelEn: 'Wednesday', icon: '📖', accent: '#d4a800', order: 2 },
  { key: 'fri', labelUa: 'П\'ятниця', labelEn: 'Friday',    icon: '✝',  accent: '#3aaa6e', order: 3 },
  { key: 'sat', labelUa: 'Субота',    labelEn: 'Saturday',  icon: '👥', accent: '#3a8fd9', order: 4 },
  { key: 'sun', labelUa: 'Неділя',    labelEn: 'Sunday',    icon: '⛪', accent: '#7c5cbf', order: 5 },
]
const DEFAULT_EVENTS = [
  { dayKey: 'tue', time: '10:00', descUa: 'Молитовне зібрання для сестер',                            descEn: 'Prayer gathering for sisters',                               order: 1 },
  { dayKey: 'tue', time: '17:00', descUa: 'Мами в молитві',                                            descEn: 'Mothers in prayer',                                          order: 2 },
  { dayKey: 'tue', time: '19:00', descUa: 'Вивчення Біблії для братів; Окремо - спілкування сестер',  descEn: 'Bible study for brothers; Separately - sisters communication', order: 3 },
  { dayKey: 'wed', time: '19:00', descUa: 'Вивчення Святого Письма для молоді',                        descEn: 'Bible study for youth',                                      order: 1 },
  { dayKey: 'fri', time: '10:00', descUa: 'Загальноцерковне Молитовне служіння',                       descEn: 'General church prayer worship',                               order: 1 },
  { dayKey: 'sat', time: '12:00', descUa: 'Аванна',                                                    descEn: 'Avanna',                                                     order: 1 },
  { dayKey: 'sat', time: '15:00', descUa: 'Підліткове служіння',                                       descEn: 'Youth worshipping',                                          order: 2 },
  { dayKey: 'sat', time: '18:00', descUa: 'Спілкування молоді',                                        descEn: 'Youth communication',                                        order: 3 },
  { dayKey: 'sun', time: '10:00', descUa: 'Загальноцерковне Ранкове зібрання',                         descEn: 'General Morning Worship',                                    order: 1 },
  { dayKey: 'sun', time: '18:00', descUa: 'Загальноцерковне Вечірнє зібрання',                         descEn: 'General Evening Worship',                                    order: 2 },
]

const emptyEventForm  = { time: '', descUa: '', descEn: '' }
const emptyNewDayForm = { weekdayKey: '', icon: '', accent: '#8b6044', events: [] }

// ── Component ─────────────────────────────────────────────────────────────────
const AdminSchedule = () => {
  const [days,    setDays]    = useState([])
  const [events,  setEvents]  = useState([])
  const [loading, setLoading] = useState(true)

  const [editDayId,   setEditDayId]   = useState(null)
  const [dayForm,     setDayForm]     = useState({})
  const [editEventId, setEditEventId] = useState(null)
  const [eventForm,   setEventForm]   = useState({})

  const [newEventDay,  setNewEventDay]  = useState(null)
  const [newEventForm, setNewEventForm] = useState(emptyEventForm)

  const [showNewDay, setShowNewDay] = useState(false)
  const [newDayForm, setNewDayForm] = useState(emptyNewDayForm)

  const [seeding,  setSeeding]  = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [deleting, setDeleting] = useState(null)

  const seededRef = useRef(false)

  // ── Firestore listeners ─────────────────────────────────────────────────────
  useEffect(() => {
    let daysLoaded = false, eventsLoaded = false
    let rawDays = [], rawEvents = []

    const tryFinish = () => {
      if (daysLoaded && eventsLoaded) setLoading(false)
    }

    const unsubDays = onSnapshot(
      query(collection(db, 'scheduleDays'), orderBy('order')),
      snap => {
        rawDays = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        setDays([...rawDays].sort((a, b) => (DAY_ORDER[a.key] ?? 99) - (DAY_ORDER[b.key] ?? 99)))
        daysLoaded = true; tryFinish()
      }
    )
    const unsubEvents = onSnapshot(
      query(collection(db, 'scheduleEvents'), orderBy('order')),
      snap => { rawEvents = snap.docs.map(d => ({ id: d.id, ...d.data() })); setEvents(rawEvents); eventsLoaded = true; tryFinish() }
    )
    return () => { unsubDays(); unsubEvents() }
  }, [])

  // ── Auto-seed when Firestore is empty ───────────────────────────────────────
  const runSeed = useCallback(async () => {
    if (seededRef.current) return
    seededRef.current = true
    setSeeding(true)
    try {
      const b1 = writeBatch(db)
      DEFAULT_DAYS.forEach(d => b1.set(doc(collection(db, 'scheduleDays')), d))
      await b1.commit()
      const b2 = writeBatch(db)
      DEFAULT_EVENTS.forEach(e => b2.set(doc(collection(db, 'scheduleEvents')), e))
      await b2.commit()
    } finally {
      setSeeding(false)
    }
  }, [])

  useEffect(() => {
    if (!loading && days.length === 0) runSeed()
  }, [loading, days.length, runSeed])

  // ── Day CRUD ─────────────────────────────────────────────────────────────────
  const startEditDay = (day) => {
    setEditDayId(day.id)
    setDayForm({ weekdayKey: day.key, icon: day.icon, accent: day.accent })
    setEditEventId(null); setNewEventDay(null)
  }

  const saveDay = async () => {
    const wd = WEEKDAYS.find(w => w.key === dayForm.weekdayKey)
    if (!wd) return
    setSaving(true)
    try {
      await updateDoc(doc(db, 'scheduleDays', editDayId), {
        key: wd.key, labelUa: wd.ua, labelEn: wd.en,
        icon: dayForm.icon || wd.icon, accent: dayForm.accent,
      })
      setEditDayId(null)
    } finally { setSaving(false) }
  }

  const deleteDay = async (day) => {
    if (!window.confirm(`Видалити "${day.labelUa}" та всі її події?`)) return
    setDeleting(day.id)
    try {
      const batch = writeBatch(db)
      batch.delete(doc(db, 'scheduleDays', day.id))
      events.filter(e => e.dayKey === day.key).forEach(e => batch.delete(doc(db, 'scheduleEvents', e.id)))
      await batch.commit()
    } finally { setDeleting(null) }
  }

  const addDay = async () => {
    const wd = WEEKDAYS.find(w => w.key === newDayForm.weekdayKey)
    if (!wd) return
    setSaving(true)
    try {
      await addDoc(collection(db, 'scheduleDays'), {
        key: wd.key, labelUa: wd.ua, labelEn: wd.en,
        icon: newDayForm.icon || wd.icon,
        accent: newDayForm.accent || wd.accent,
        order: days.length + 1,
      })
      const validEvents = newDayForm.events.filter(e => e.time || e.descUa)
      if (validEvents.length > 0) {
        const batch = writeBatch(db)
        validEvents.forEach((ev, idx) => {
          batch.set(doc(collection(db, 'scheduleEvents')), {
            dayKey: wd.key, time: ev.time, descUa: ev.descUa, descEn: ev.descEn, order: idx + 1,
          })
        })
        await batch.commit()
      }
      setShowNewDay(false); setNewDayForm(emptyNewDayForm)
    } finally { setSaving(false) }
  }

  // ── Event CRUD ───────────────────────────────────────────────────────────────
  const startEditEvent = (ev) => {
    setEditEventId(ev.id)
    setEventForm({ time: ev.time, descUa: ev.descUa, descEn: ev.descEn })
    setEditDayId(null); setNewEventDay(null)
  }

  const saveEvent = async () => {
    if (!eventForm.descUa.trim()) return
    setSaving(true)
    try {
      await updateDoc(doc(db, 'scheduleEvents', editEventId), {
        time: eventForm.time, descUa: eventForm.descUa.trim(), descEn: eventForm.descEn.trim(),
      })
      setEditEventId(null)
    } finally { setSaving(false) }
  }

  const deleteEvent = async (ev) => {
    setDeleting(ev.id)
    try {
      await deleteDoc(doc(db, 'scheduleEvents', ev.id))
      if (editEventId === ev.id) setEditEventId(null)
    } finally { setDeleting(null) }
  }

  const addEvent = async (day) => {
    if (!newEventForm.descUa.trim()) return
    setSaving(true)
    const dayEventsCount = events.filter(e => e.dayKey === day.key).length
    try {
      await addDoc(collection(db, 'scheduleEvents'), {
        dayKey: day.key, time: newEventForm.time,
        descUa: newEventForm.descUa.trim(), descEn: newEventForm.descEn.trim(),
        order: dayEventsCount + 1,
      })
      setNewEventDay(null); setNewEventForm(emptyEventForm)
    } finally { setSaving(false) }
  }

  // ── New day form helpers ──────────────────────────────────────────────────────
  const selectWeekday = (key) => {
    const wd = WEEKDAYS.find(w => w.key === key)
    setNewDayForm(f => ({
      ...f, weekdayKey: key,
      icon:   wd ? wd.icon   : f.icon,
      accent: wd ? wd.accent : f.accent,
    }))
  }

  const addNewDayEvent = () =>
    setNewDayForm(f => ({ ...f, events: [...f.events, { time: '', descUa: '', descEn: '' }] }))

  const updateNewDayEvent = (idx, field, val) =>
    setNewDayForm(f => {
      const evs = [...f.events]; evs[idx] = { ...evs[idx], [field]: val }; return { ...f, events: evs }
    })

  const removeNewDayEvent = (idx) =>
    setNewDayForm(f => ({ ...f, events: f.events.filter((_, i) => i !== idx) }))

  const selectedWd = WEEKDAYS.find(w => w.key === newDayForm.weekdayKey)

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div style={s.page}>

        <div style={s.header}>
          <h1 style={s.title}>📅 Планування</h1>
          <button style={s.seedBtn} onClick={() => { seededRef.current = false; runSeed() }} disabled={seeding}>
            {seeding ? 'Завантаження…' : '⬇ Типові дані'}
          </button>
        </div>
        <p style={s.hint}>Зміни одразу відображаються на публічній сторінці розкладу.</p>

        {seeding && <div style={s.seedingBanner}>⏳ Завантаження типових даних…</div>}

        {/* Day cards */}
        {days.map(day => {
          const dayEvents = events.filter(e => e.dayKey === day.key).sort((a, b) => a.order - b.order)
          const isEditingDay = editDayId === day.id
          const editWd = WEEKDAYS.find(w => w.key === dayForm.weekdayKey)

          return (
            <div key={day.id} style={s.card}>
              {/* Day header */}
              <div style={{ ...s.cardHead, background: isEditingDay ? '#f5ede3' : day.accent }}>
                {isEditingDay ? (
                  <div style={s.editRow}>
                    <select
                      style={{ ...s.input, flex: 1 }}
                      value={dayForm.weekdayKey}
                      onChange={e => {
                        const wd = WEEKDAYS.find(w => w.key === e.target.value)
                        setDayForm(f => ({ ...f, weekdayKey: e.target.value, icon: wd ? wd.icon : f.icon, accent: wd ? wd.accent : f.accent }))
                      }}
                    >
                      <option value=''>— день —</option>
                      {WEEKDAYS.map(w => <option key={w.key} value={w.key}>{w.ua} / {w.en}</option>)}
                    </select>
                    <input style={{ ...s.input, width: '52px' }} value={dayForm.icon}
                      onChange={e => setDayForm(f => ({ ...f, icon: e.target.value }))} placeholder={editWd?.icon || '📅'} />
                    <input type='color' style={s.colorPicker} value={dayForm.accent}
                      onChange={e => setDayForm(f => ({ ...f, accent: e.target.value }))} />
                    <button style={s.btnSm} onClick={saveDay} disabled={saving}>✓</button>
                    <button style={{ ...s.btnSm, ...s.btnGhost }} onClick={() => setEditDayId(null)}>✕</button>
                  </div>
                ) : (
                  <div style={s.dayHeadRow}>
                    <span style={s.dayIcon}>{day.icon}</span>
                    <span style={s.dayLabel}>{day.labelUa}</span>
                    {day.labelEn && <span style={s.dayLabelEn}>/ {day.labelEn}</span>}
                    <div style={s.dayActions}>
                      <button style={s.btnSmWhite} onClick={() => startEditDay(day)}>✏</button>
                      <button style={{ ...s.btnSmWhite, ...s.btnDangerWhite }} onClick={() => deleteDay(day)} disabled={deleting === day.id}>✕</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Events list */}
              <div style={s.cardBody}>
                {dayEvents.map(ev => (
                  <div key={ev.id}>
                    {editEventId === ev.id ? (
                      <div style={s.editRow}>
                        <input type='time' style={{ ...s.input, width: '110px' }} value={eventForm.time}
                          onChange={e => setEventForm(f => ({ ...f, time: e.target.value }))} />
                        <input style={{ ...s.input, flex: 1 }} value={eventForm.descUa} placeholder='Опис (UA)'
                          onChange={e => setEventForm(f => ({ ...f, descUa: e.target.value }))} />
                        <input style={{ ...s.input, flex: 1 }} value={eventForm.descEn} placeholder='Description (EN)'
                          onChange={e => setEventForm(f => ({ ...f, descEn: e.target.value }))} />
                        <button style={s.btnSm} onClick={saveEvent} disabled={saving}>✓</button>
                        <button style={{ ...s.btnSm, ...s.btnGhost }} onClick={() => setEditEventId(null)}>✕</button>
                      </div>
                    ) : (
                      <div style={s.eventRow}>
                        <span style={{ ...s.timeBadge, background: day.accent }}>{ev.time || '—'}</span>
                        <div style={s.eventTexts}>
                          <span style={s.eventUa}>{ev.descUa}</span>
                          {ev.descEn && <span style={s.eventEn}>{ev.descEn}</span>}
                        </div>
                        <div style={s.eventBtns}>
                          <button style={s.btnXs} onClick={() => startEditEvent(ev)}>✏</button>
                          <button style={{ ...s.btnXs, ...s.btnDanger }} onClick={() => deleteEvent(ev)} disabled={deleting === ev.id}>✕</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* New event form */}
                {newEventDay === day.id ? (
                  <div style={{ ...s.editRow, marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e8d8c8' }}>
                    <input type='time' style={{ ...s.input, width: '110px' }} value={newEventForm.time} autoFocus
                      onChange={e => setNewEventForm(f => ({ ...f, time: e.target.value }))} />
                    <input style={{ ...s.input, flex: 1 }} value={newEventForm.descUa} placeholder='Опис (UA)'
                      onChange={e => setNewEventForm(f => ({ ...f, descUa: e.target.value }))} />
                    <input style={{ ...s.input, flex: 1 }} value={newEventForm.descEn} placeholder='Description (EN)'
                      onChange={e => setNewEventForm(f => ({ ...f, descEn: e.target.value }))} />
                    <button style={s.btnSm} onClick={() => addEvent(day)} disabled={saving}>✓</button>
                    <button style={{ ...s.btnSm, ...s.btnGhost }} onClick={() => setNewEventDay(null)}>✕</button>
                  </div>
                ) : (
                  <button style={s.addEventBtn}
                    onClick={() => { setNewEventDay(day.id); setNewEventForm(emptyEventForm); setEditEventId(null); setEditDayId(null) }}>
                    + Додати подію
                  </button>
                )}
              </div>
            </div>
          )
        })}

        {/* Add new day */}
        {showNewDay ? (
          <div style={s.newDayCard}>
            <div style={s.newDayTitle}>Новий день</div>

            <div style={s.editRow}>
              <select style={{ ...s.input, flex: 1 }} value={newDayForm.weekdayKey} onChange={e => selectWeekday(e.target.value)}>
                <option value=''>— оберіть день —</option>
                {WEEKDAYS.map(w => <option key={w.key} value={w.key}>{w.ua} / {w.en}</option>)}
              </select>
              <input style={{ ...s.input, width: '52px' }} value={newDayForm.icon} placeholder={selectedWd?.icon || '📅'}
                onChange={e => setNewDayForm(f => ({ ...f, icon: e.target.value }))} />
              <input type='color' style={s.colorPicker} value={newDayForm.accent}
                onChange={e => setNewDayForm(f => ({ ...f, accent: e.target.value }))} />
            </div>

            {/* Events within new day */}
            {newDayForm.events.length > 0 && (
              <div style={s.newDayEvents}>
                <div style={s.newDayEventsLabel}>Події цього дня</div>
                {newDayForm.events.map((ev, idx) => (
                  <div key={idx} style={{ ...s.editRow, marginBottom: '6px' }}>
                    <input type='time' style={{ ...s.input, width: '110px' }} value={ev.time}
                      onChange={e => updateNewDayEvent(idx, 'time', e.target.value)} />
                    <input style={{ ...s.input, flex: 1 }} value={ev.descUa} placeholder='Опис (UA)'
                      onChange={e => updateNewDayEvent(idx, 'descUa', e.target.value)} />
                    <input style={{ ...s.input, flex: 1 }} value={ev.descEn} placeholder='Description (EN)'
                      onChange={e => updateNewDayEvent(idx, 'descEn', e.target.value)} />
                    <button style={{ ...s.btnSm, ...s.btnGhost }} onClick={() => removeNewDayEvent(idx)}>✕</button>
                  </div>
                ))}
              </div>
            )}

            <button style={s.addEventInDayBtn} onClick={addNewDayEvent}>
              + Додати подію
            </button>

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button style={s.btnPrimary} onClick={addDay} disabled={saving || !newDayForm.weekdayKey}>
                {saving ? 'Збереження…' : 'Зберегти день'}
              </button>
              <button style={s.btnGhostLg} onClick={() => { setShowNewDay(false); setNewDayForm(emptyNewDayForm) }}>
                Скасувати
              </button>
            </div>
          </div>
        ) : (
          <button style={s.addDayBtn} onClick={() => setShowNewDay(true)}>
            + Додати день
          </button>
        )}

      </div>
    </AdminLayout>
  )
}

export default AdminSchedule

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:   { maxWidth: '820px' },
  header: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px', flexWrap: 'wrap' },
  title:  { fontSize: '22px', fontWeight: '700', color: '#2d1f14', margin: 0 },
  hint:   { fontSize: '13px', color: '#9a7c63', marginBottom: '24px', marginTop: '4px' },

  seedBtn: {
    padding: '8px 16px', background: '#f0e4d4', border: '1.5px solid #c4986a',
    borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#8b6044',
    fontWeight: '600', fontFamily: 'inherit',
  },
  seedingBanner: {
    background: '#fff9ec', border: '1px solid #f0c060', borderRadius: '10px',
    padding: '12px 16px', fontSize: '14px', color: '#8b6044', marginBottom: '16px',
  },

  card: {
    background: '#fff', borderRadius: '14px', overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(92,61,46,0.10)', border: '1px solid #f0e4d4', marginBottom: '16px',
  },
  cardHead: { padding: '12px 16px' },
  cardBody: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' },

  dayHeadRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  dayIcon:    { fontSize: '18px', lineHeight: 1 },
  dayLabel:   { fontSize: '16px', fontWeight: '700', color: '#fff', letterSpacing: '0.3px', textTransform: 'uppercase' },
  dayLabelEn: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' },
  dayActions: { marginLeft: 'auto', display: 'flex', gap: '6px' },

  editRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },

  eventRow: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '6px 0', borderBottom: '1px solid #f5ede3',
  },
  eventBtns:  { display: 'flex', gap: '4px', flexShrink: 0, marginLeft: 'auto' },
  eventTexts: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  eventUa:    { fontSize: '14px', color: '#2d1f14', lineHeight: '1.4' },
  eventEn:    { fontSize: '12px', color: '#9a7c63', lineHeight: '1.4', fontStyle: 'italic' },

  timeBadge: {
    flexShrink: 0, color: '#fff', fontSize: '12px', fontWeight: '700',
    padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.5px', marginTop: '2px',
  },

  addEventBtn: {
    background: 'none', border: '1.5px dashed #e8d8c8', borderRadius: '8px',
    padding: '7px 14px', cursor: 'pointer', fontSize: '13px', color: '#9a7c63',
    width: '100%', textAlign: 'center', marginTop: '4px', fontFamily: 'inherit',
  },
  addDayBtn: {
    display: 'block', width: '100%', padding: '14px',
    background: '#fff', border: '2px dashed #c4986a', borderRadius: '14px',
    cursor: 'pointer', fontSize: '15px', color: '#8b6044', fontWeight: '600',
    textAlign: 'center', fontFamily: 'inherit',
  },

  newDayCard: {
    background: '#fff', borderRadius: '14px', padding: '20px',
    border: '2px solid #c4986a', marginBottom: '16px',
  },
  newDayTitle:      { fontSize: '15px', fontWeight: '700', color: '#2d1f14', marginBottom: '12px' },
  newDayEvents:     { marginTop: '14px', marginBottom: '6px' },
  newDayEventsLabel:{ fontSize: '12px', fontWeight: '600', color: '#9a7c63', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' },
  addEventInDayBtn: {
    background: 'none', border: '1.5px dashed #e8d8c8', borderRadius: '8px',
    padding: '6px 14px', cursor: 'pointer', fontSize: '13px', color: '#9a7c63',
    marginTop: '4px', fontFamily: 'inherit',
  },

  input: {
    padding: '7px 10px', border: '1.5px solid #e8d8c8', borderRadius: '7px',
    fontSize: '13px', color: '#2d1f14', fontFamily: 'inherit', outline: 'none', minWidth: 0,
  },
  colorPicker: {
    width: '36px', height: '34px', padding: '2px', border: '1.5px solid #e8d8c8',
    borderRadius: '7px', cursor: 'pointer', background: 'none',
  },

  btnSm: {
    padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600', background: '#8b6044', color: '#fff',
    fontFamily: 'inherit', flexShrink: 0,
  },
  btnSmWhite: {
    padding: '4px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
    fontSize: '13px', background: 'rgba(255,255,255,0.25)', color: '#fff',
    fontFamily: 'inherit', flexShrink: 0,
  },
  btnXs: {
    padding: '4px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer',
    fontSize: '12px', background: '#f5ede3', color: '#8b6044', fontFamily: 'inherit', flexShrink: 0,
  },
  btnGhost:       { background: '#f5ede3', color: '#8b6044' },
  btnDanger:      { background: 'rgba(220,50,50,0.12)', color: '#c0392b' },
  btnDangerWhite: { background: 'rgba(220,50,50,0.25)', color: '#fff' },

  btnPrimary: {
    padding: '9px 20px', background: '#8b6044', color: '#fff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit',
  },
  btnGhostLg: {
    padding: '9px 20px', background: '#f5ede3', color: '#8b6044', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit',
  },
}
